import { ChevronUp, Clock, Loader2, Share2, Timer, X, Zap } from 'lucide-react';
import React, { useState } from 'react';

import {
    Drawer,
    DrawerContent,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import { type Coin, useTokenPairStore } from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import { useCloseActiveTrade } from '../hooks/useCloseActiveTrade';
import { useExecutionDisplay } from '../hooks/useExecutionDisplay';
import { useLimitOrderActions } from '../hooks/useLimitOrderActions';
import type { PriceConfig } from '../hooks/usePriceConfig';
import { type TerminalRow, useTerminalRows } from '../hooks/useTerminalRows';
import { useTimeFormat } from '../hooks/useTimeFormat';
import {
    selectOpenPositionCount,
    selectTotalUnrealizedPnl,
    useTradingStore,
} from '../store';
import { formatRowTime, formatTimestampTitle } from '../utils/tradeTime';

import PnlShareDialog, { type PnlShareRequest } from './PnlShareDialog';
import {
    ChartedPnlCell,
    OffChartPnlCell,
    RestingDistanceCell,
} from './PositionPnlCells';

interface MobilePositionsSheetProps {
    priceConfig: PriceConfig;
    /** Closes the CHARTED market's position, through the rail's close flow. */
    onClosePosition: () => void;
    /** Cancels the CHARTED market's resting order, through the same flow. */
    onCancelLimitOrder: () => void;
    /** Fills the CHARTED market's resting order, through the same flow. */
    onFillLimitOrder: () => void;
}

/**
 * Positions and history on mobile, where the desktop `SpeedTerminal` does not
 * mount.
 *
 * The gap this fills was not cosmetic: history lived five rows deep inside the
 * "Quick Actions" drawer, and positions in markets other than the charted one
 * had no representation at all — a phone could neither see nor close them.
 * Both ledgers are the same rows the terminal draws (`useTerminalRows`), so the
 * two surfaces cannot drift apart.
 */
const MobilePositionsSheet: React.FC<MobilePositionsSheetProps> = ({
    priceConfig,
    onClosePosition,
    onCancelLimitOrder,
    onFillLimitOrder,
}) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<'positions' | 'limits' | 'history'>(
        'positions',
    );
    const [shareRequest, setShareRequest] = useState<PnlShareRequest | null>(
        null,
    );

    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const setSelectedCoin = useTokenPairStore((s) => s.setSelectedCoin);
    const openCount = useTradingStore(selectOpenPositionCount);
    const totalPnl = useTradingStore(selectTotalUnrealizedPnl);
    const { closeTrade, closeStates } = useCloseActiveTrade();
    const { cancelOrder, fillOrder, orderStates } = useLimitOrderActions();
    // the charted row shares the rail's in-flight state, since it shares its
    // handlers — one order, one spinner, wherever it is pressed from.
    const { executionInFlight } = useExecutionDisplay();
    const {
        positionRows,
        historyRows,
        limitRows,
        historyErrored,
        resolveShare,
    } = useTerminalRows(priceConfig);
    // Same preference the desktop terminal's Time column writes.
    const { format: timeFormat, toggle: toggleTimeFormat } = useTimeFormat();

    const rows =
        tab === 'positions'
            ? positionRows
            : tab === 'limits'
                ? limitRows
                : historyRows;

    const openShare = (row: TerminalRow) => {
        const request = resolveShare(row);
        if (request) setShareRequest(request);
    };

    /** Chart this row's market and get out of the way. */
    const retarget = (coin: string) => {
        setSelectedCoin(coin as Coin);
        setOpen(false);
    };

    return (
        <>
            <Drawer
                open={open}
                onOpenChange={(next) => {
                    // Opening with nothing held goes straight to what there is.
                    if (next && positionRows.length === 0)
                        setTab(limitRows.length > 0 ? 'limits' : 'history');
                    setOpen(next);
                }}
            >
                {/* Peek bar — the persistent entry point. It stays mounted with
                    no position so the ledger is always in the same place, and
                    carries the aggregate PnL so it earns its row unopened. */}
                <DrawerTrigger asChild>
                    <button
                        type="button"
                        aria-label="Open positions and history"
                        className={cn(
                            'flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl border px-3 py-2 transition-colors',
                            openCount > 0
                                ? 'border-primary/30 bg-primary/[0.06] hover:bg-primary/10'
                                : 'border-border bg-secondary/30 hover:bg-secondary/50',
                        )}
                    >
                        <span className="flex items-center gap-2">
                            <ChevronUp
                                size={12}
                                className={
                                    openCount > 0
                                        ? 'text-primary'
                                        : 'text-muted-foreground'
                                }
                            />
                            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
                                {openCount > 0
                                    ? 'Positions'
                                    : limitRows.length > 0
                                        ? 'Limit orders'
                                        : 'History'}
                            </span>
                            {openCount > 0 && (
                                <>
                                    <span className="rounded bg-primary px-1.5 font-mono text-[9px] font-bold text-primary-foreground">
                                        {openCount}
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                                        · History
                                    </span>
                                </>
                            )}
                        </span>
                        {totalPnl !== null && openCount > 0 ? (
                            <PnlFigureValue value={totalPnl} />
                        ) : (
                            <span className="font-mono text-[10px] text-muted-foreground">
                                {limitRows.length > 0
                                    ? limitRows.length
                                    : historyRows.length}
                            </span>
                        )}
                    </button>
                </DrawerTrigger>

                <DrawerContent className="h-[72vh]">
                    <DrawerTitle className="sr-only">
                        Positions and trade history
                    </DrawerTitle>

                    {/* Tabs */}
                    <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 pb-2">
                        {(
                            [
                                ['positions', 'Positions', positionRows.length],
                                ['limits', 'Limit orders', limitRows.length],
                                ['history', 'History', historyRows.length],
                            ] as const
                        ).map(([key, label, count]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setTab(key)}
                                aria-pressed={tab === key}
                                className={cn(
                                    'flex cursor-pointer items-center gap-1 border-b-2 pb-2 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors',
                                    tab === key
                                        ? 'border-primary text-foreground'
                                        : 'border-transparent text-muted-foreground',
                                )}
                                style={{ marginBottom: '-2px' }}
                            >
                                {label}
                                <span
                                    className={cn(
                                        'rounded-full px-1.5 py-px font-mono text-[9px] font-bold',
                                        tab === key
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-muted-foreground',
                                    )}
                                >
                                    {count}
                                </span>
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={toggleTimeFormat}
                            aria-pressed={timeFormat === 'relative'}
                            title={
                                timeFormat === 'relative'
                                    ? 'Show the date and clock time'
                                    : 'Show how long ago'
                            }
                            className="ml-auto flex cursor-pointer items-center gap-1 pb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                        >
                            {timeFormat === 'relative' ? (
                                <Timer size={10} />
                            ) : (
                                <Clock size={10} />
                            )}
                            Time
                        </button>
                    </div>

                    {/* Rows */}
                    <div className="flex-1 overflow-y-auto overscroll-contain">
                        {rows.length === 0 ? (
                            <p className="px-4 py-8 text-center font-mono text-[11px] text-muted-foreground">
                                {tab === 'positions'
                                    ? 'No open positions.'
                                    : tab === 'limits'
                                        ? historyErrored
                                            ? 'Limit order history is unavailable right now.'
                                            : 'No limit orders yet. Choose Limit below leverage to place one.'
                                        : historyErrored
                                            ? "Couldn't load your history."
                                            : 'No closed trades yet.'}
                            </p>
                        ) : (
                            rows.map((row) => {
                                const closing =
                                    !!row.tradeHash &&
                                    closeStates.get(row.tradeHash) ===
                                        'closing';
                                const orderBusy =
                                    orderStates.get(row.key) === 'busy';
                                const canRetarget =
                                    !!row.coin &&
                                    row.coin.toUpperCase() !==
                                        selectedCoin.toUpperCase();
                                const isCharted =
                                    !!row.coin &&
                                    row.coin.toUpperCase() ===
                                        selectedCoin.toUpperCase();
                                return (
                                    <div
                                        key={row.key}
                                        {...(canRetarget && {
                                            role: 'button',
                                            tabIndex: 0,
                                            'aria-label': `Show ${row.market} on the chart`,
                                            onClick: () => retarget(row.coin!),
                                            onKeyDown: (
                                                event: React.KeyboardEvent<HTMLDivElement>,
                                            ) => {
                                                if (
                                                    event.key === 'Enter' ||
                                                    event.key === ' '
                                                ) {
                                                    event.preventDefault();
                                                    retarget(row.coin!);
                                                }
                                            },
                                        })}
                                        className={cn(
                                            'flex items-center gap-3 border-b border-border/40 px-4 py-2.5',
                                            canRetarget && 'cursor-pointer',
                                            // the market already on the chart
                                            // wears the same lime spine as the
                                            // desktop terminal and the sidebar.
                                            tab !== 'history' &&
                                                isCharted &&
                                                'bg-primary/[0.06] shadow-[inset_2px_0_0_0_var(--primary)]',
                                        )}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="truncate text-xs font-semibold">
                                                    {row.market}
                                                </span>
                                                <span
                                                    className={cn(
                                                        'rounded px-1 py-px font-mono text-[8px] font-bold uppercase tracking-[0.06em]',
                                                        row.side === 'LONG'
                                                            ? 'bg-primary/15 text-primary'
                                                            : 'bg-destructive/15 text-destructive',
                                                    )}
                                                >
                                                    {row.side} {row.leverage}
                                                </span>
                                                {row.status && (
                                                    <span
                                                        className={cn(
                                                            'font-mono text-[8px] font-semibold uppercase tracking-[0.08em]',
                                                            row.status
                                                                .className,
                                                        )}
                                                    >
                                                        {row.status.label}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 truncate font-mono text-[9px] tabular-nums text-muted-foreground">
                                                <span
                                                    title={formatTimestampTitle(
                                                        row.at,
                                                    )}
                                                >
                                                    {formatRowTime(
                                                        row.at,
                                                        timeFormat,
                                                    )}
                                                </span>{' '}
                                                · {row.margin} margin · entry{' '}
                                                {row.entry}
                                            </div>
                                        </div>

                                        <div className="shrink-0 font-mono text-xs tabular-nums">
                                            {row.resting ? (
                                                <RestingDistanceCell
                                                    coin={row.coin ?? ''}
                                                    limitPrice={
                                                        row.limitPrice ?? 0
                                                    }
                                                />
                                            ) : row.live === 'charted' ? (
                                                <ChartedPnlCell stacked />
                                            ) : row.live === 'offChart' &&
                                              row.coin ? (
                                                    <OffChartPnlCell
                                                        coin={row.coin}
                                                        stacked
                                                    />
                                                ) : row.pnl !== null ? (
                                                    <span
                                                        className={cn(
                                                            'font-bold',
                                                            row.pnlPositive
                                                                ? 'text-success'
                                                                : 'text-destructive',
                                                        )}
                                                    >
                                                        {row.pnl}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">
                                                    —
                                                    </span>
                                                )}
                                        </div>

                                        {/* Row actions must not bubble into the
                                            retarget: Close closes the trade, it
                                            does not also swap the chart. */}
                                        <span
                                            className="flex shrink-0 items-center gap-1"
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                            onKeyDown={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            {(row.share || row.shareDemo) && (
                                                <button
                                                    type="button"
                                                    aria-label={`Share ${row.market} PnL`}
                                                    onClick={() =>
                                                        openShare(row)
                                                    }
                                                    className="cursor-pointer p-1.5 text-muted-foreground"
                                                >
                                                    <Share2 size={13} />
                                                </button>
                                            )}
                                            {/* ONLY the charted row closes through
                                                the rail — `onClosePosition` targets
                                                the charted market, so an off-chart
                                                row using it would close the wrong
                                                position. Those carry their own
                                                hash and coin. */}
                                            {/* the CHARTED order goes through
                                                the rail's handlers, like the
                                                charted close: acting on it from
                                                here would strand the execution
                                                slot that drives the rail. */}
                                            {row.resting && isCharted ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        aria-label={`Fill ${row.market} limit order now`}
                                                        disabled={
                                                            executionInFlight
                                                        }
                                                        onClick={() => {
                                                            onFillLimitOrder();
                                                            setOpen(false);
                                                        }}
                                                        className={cn(
                                                            'cursor-pointer p-1.5',
                                                            executionInFlight
                                                                ? 'cursor-wait text-muted-foreground'
                                                                : 'text-muted-foreground',
                                                        )}
                                                    >
                                                        {executionInFlight ? (
                                                            <Loader2
                                                                size={13}
                                                                className="animate-spin"
                                                            />
                                                        ) : (
                                                            <Zap size={13} />
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        aria-label={`Cancel ${row.market} limit order`}
                                                        disabled={
                                                            executionInFlight
                                                        }
                                                        onClick={() => {
                                                            onCancelLimitOrder();
                                                            setOpen(false);
                                                        }}
                                                        className={cn(
                                                            'cursor-pointer p-1.5',
                                                            executionInFlight
                                                                ? 'cursor-wait text-muted-foreground'
                                                                : 'text-destructive',
                                                        )}
                                                    >
                                                        <X size={13} />
                                                    </button>
                                                </>
                                            ) : row.resting && row.coin ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        aria-label={`Fill ${row.market} limit order now`}
                                                        disabled={orderBusy}
                                                        onClick={() => {
                                                            void fillOrder(
                                                                row.key,
                                                                row.coin!,
                                                                row.tradeHash,
                                                            );
                                                        }}
                                                        className={cn(
                                                            'cursor-pointer p-1.5',
                                                            orderBusy
                                                                ? 'cursor-wait text-muted-foreground'
                                                                : 'text-muted-foreground',
                                                        )}
                                                    >
                                                        {orderBusy ? (
                                                            <Loader2
                                                                size={13}
                                                                className="animate-spin"
                                                            />
                                                        ) : (
                                                            <Zap size={13} />
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        aria-label={`Cancel ${row.market} limit order`}
                                                        disabled={orderBusy}
                                                        onClick={() => {
                                                            void cancelOrder(
                                                                row.key,
                                                                row.coin!,
                                                                row.tradeHash,
                                                            );
                                                        }}
                                                        className={cn(
                                                            'cursor-pointer p-1.5',
                                                            orderBusy
                                                                ? 'cursor-wait text-muted-foreground'
                                                                : 'text-destructive',
                                                        )}
                                                    >
                                                        <X size={13} />
                                                    </button>
                                                </>
                                            ) : row.live === 'charted' ? (
                                                <button
                                                    type="button"
                                                    aria-label={`Close ${row.market} position`}
                                                    onClick={() => {
                                                        onClosePosition();
                                                        setOpen(false);
                                                    }}
                                                    className="cursor-pointer p-1.5 text-destructive"
                                                >
                                                    <X size={13} />
                                                </button>
                                            ) : row.live === 'offChart' &&
                                              row.tradeHash &&
                                              row.coin ? (
                                                    <button
                                                        type="button"
                                                        aria-label={`Close ${row.market} position`}
                                                        disabled={closing}
                                                        onClick={() => {
                                                            void closeTrade(
                                                            row.tradeHash!,
                                                            row.coin!,
                                                            );
                                                        }}
                                                        className={cn(
                                                            'cursor-pointer p-1.5',
                                                            closing
                                                                ? 'cursor-wait text-muted-foreground'
                                                                : 'text-destructive',
                                                        )}
                                                    >
                                                        {closing ? (
                                                            <Loader2
                                                                size={13}
                                                                className="animate-spin"
                                                            />
                                                        ) : (
                                                            <X size={13} />
                                                        )}
                                                    </button>
                                                ) : null}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="flex shrink-0 items-center justify-between border-t border-border px-4 py-2.5 font-mono text-[9px] uppercase tracking-[0.08em] text-muted-foreground">
                        {tab === 'limits' ? (
                            <>
                                <span>{limitRows.length} limit orders</span>
                                <span>Pending · Running · Closed</span>
                            </>
                        ) : tab === 'positions' ? (
                            <>
                                <span>
                                    {totalPnl !== null ? (
                                        <>Net {formatSignedUsd(totalPnl)}</>
                                    ) : (
                                        <>{positionRows.length} open</>
                                    )}
                                </span>
                                <span>Tap a row to chart it</span>
                            </>
                        ) : (
                            <>
                                <span>{historyRows.length} trades</span>
                                <span>Share opens the PnL card</span>
                            </>
                        )}
                    </div>
                </DrawerContent>
            </Drawer>

            {shareRequest && (
                <PnlShareDialog
                    request={shareRequest}
                    onDismiss={() => setShareRequest(null)}
                />
            )}
        </>
    );
};

/** Aggregate PnL on the peek bar — value only; the count sits beside it. */
const PnlFigureValue: React.FC<{ value: number }> = ({ value }) => (
    <span
        className={cn(
            'font-mono text-[11px] font-bold tabular-nums',
            value >= 0 ? 'text-success' : 'text-destructive',
        )}
    >
        {formatSignedUsd(value)}
    </span>
);

function formatSignedUsd(value: number): string {
    return `${value >= 0 ? '+' : '-'}$${Math.abs(value).toFixed(2)}`;
}

export default MobilePositionsSheet;
